<template>
  <div>
    <div
      class="tabs is-medium"
      v-if="!autoHide || Object.keys(tabs).length > 1"
    >
      <ul>
        <li
          v-for="(displayName, key) in tabs"
          :key="key"
          :class="{ 'is-active': selected === key }"
          v-on:click="select(key)"
        >
          <a>{{ displayName }}</a>
        </li>
      </ul>
    </div>

    <slot :name="selected"></slot>
  </div>
</template>

<script lang="ts">
import { type PropType, defineComponent } from 'vue'

/**
 * A generic component for Bulma tabs.
 * Usage:
 * ```
 * <Tabs :tabs="{ tab1: 'Tab 1', tab2: 'Tab 2' }">
 *   <template v-slot:tab1>
 *     The first tab
 *   </template>
 *   <template v-slot:tab2>
 *     The second tab
 *   </template>
 * </Tabs>
 * ```
 */
export default defineComponent({
  emits: {
    'update:modelValue': (value: string) => typeof value === 'string',
  },
  props: {
    /**
     * The tabs to display, as an object that maps a key to a display name:
     * `{tab1: 'First tab', tab2: 'Second tab'}`
     */
    tabs: {
      type: Object as PropType<Record<string, string>>,
      required: true,
      // Ensure the object has string values
      validator: (tabs: unknown): boolean =>
        typeof tabs === 'object' &&
        tabs !== null &&
        Object.values(tabs).every((value) => typeof value === 'string'),
    },
    /**
     * Automatically hide the tabs if there is only one tab.
     */
    autoHide: {
      type: Boolean,
      default: false,
    },
    /**
     * Allows using v-model to access or change the currently selected tab in a parent component.
     */
    modelValue: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    selected: '',
  }),
  methods: {
    select(tab: string) {
      /*
       * Either there are no defined tabs at all, and we can select the default empty value,
       * or there are tabs and we can only select a tab that exists.
       */
      if ((Object.keys(this.tabs).length > 0 || tab) && !this.tabs[tab])
        throw new Error(`Unknown tab ${tab}`)
      this.selected = tab
      this.$emit('update:modelValue', tab)
    },
  },
  watch: {
    /*
     * If the defined tabs change, ensure the selected tab exists or is set to its default empty value
     */
    tabs: {
      immediate: true,
      handler(newValue: Record<string, string>) {
        if (!newValue) this.select('')
        else if (!newValue[this.selected]) this.select(Object.keys(newValue)[0] ?? '')
      },
    },
    /*
     * Switch tabs if the value was updated from the parent component
     */
    modelValue: {
      immediate: true,
      handler(newValue: string) {
        if (newValue && this.selected !== newValue) this.select(newValue)
      },
    },
  },
})
</script>
